/**
 * SpaceTabsBar — AutoCAD-style Model / Layout tab bar.
 * Renders at the bottom of the canvas area, above the command strip.
 *
 * Phase MC1: tab switching, drag-reorder (HTML5 native), context menu stubs.
 * Phase MC2: PaperSpaceLayer visual (separate PR).
 * Phase MC3: double-click viewport → enterViewport (separate PR).
 */

import { useState, useRef, useCallback } from "react";
import { useEditorStore } from "../state/editorStore";
import { useSpaceContextStore } from "../state/spaceContextStore";
import { useT } from "../i18n";
import { SpaceModeBadge } from "./StatusStrip/SpaceModeBadge";
import { ViewportStatusControls } from "./StatusStrip/ViewportStatusControls";
import { toTestIdSegment } from "../helpers/formatHelpers";
import { usePublishLayouts } from "../hooks/usePublishLayouts";
import type { PdfExportConfig } from "../print/pdf/pdfExportConfig";
import {
  resolvePublishOrder,
  selectRange,
  toggleSelection,
} from "./spaceTabSelection";

interface ContextMenuState {
  spaceId: string;
  spaceName: string;
  x: number;
  y: number;
}

// PDF-6.7 Codex P2 r3 — tabs inert during PLOT Window pick; otherwise switching
// tabs mid-pick changes the live camera frame and the persisted window ends up
// in the wrong layout's mm space.
export interface SpaceTabsBarProps {
  windowPickActive?: boolean;
  /**
   * PDF-6.9b — non-reactive read of the committed PDF Options config.
   * Threaded into `usePublishLayouts` so the multi-sheet PUBLISH honours the
   * user's vector DPI / omitted-layers / bookmarks choice. Optional — when
   * absent the publish path uses {@link defaultPdfExportConfig}.
   */
  getPdfOptionsConfig?: () => PdfExportConfig;
}

export function SpaceTabsBar({
  windowPickActive = false,
  getPdfOptionsConfig,
}: SpaceTabsBarProps = {}) {
  const t = useT();
  const dispatch = useEditorStore((s) => s.dispatch);
  const spaces = useEditorStore((s) => s.state.drawing.spaces);
  const activeSpaceId = useEditorStore((s) => s.state.drawing.activeSpaceId);
  const { layoutOrder, setActiveSpace, reorderLayouts } =
    useSpaceContextStore();

  const [renamingSpaceId, setRenamingSpaceId] = useState<string | null>(null);
  const [renamingDraftName, setRenamingDraftName] = useState<string>("");
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [dragFromId, setDragFromId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  // PDF-6.8 — layout-tab multi-select for PUBLISH. `selected` holds LAYOUT space
  // ids only (the Model tab is never part of a publish selection); `anchor` is
  // the last plain-clicked / ctrl-clicked layout, used as the shift-range pivot.
  const [selected, setSelected] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const [selectionAnchor, setSelectionAnchor] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { publishSelected } = usePublishLayouts({ getPdfOptionsConfig });

  // Build ordered space list: layoutOrder drives order, spaces provides names.
  const spaceMap = new Map(spaces.map((s) => [s.id, s]));
  const orderedSpaces = layoutOrder
    .map((id) => spaceMap.get(id))
    .filter(Boolean) as typeof spaces;
  // Append any spaces not yet in layoutOrder (e.g. newly added layout).
  // Filter by kind === "layout" only — NOT "model" — because the shell
  // contract normalizes block-edit / block-test / refedit synthetic spaces
  // to kind "model", and those must never appear in the visible tab strip.
  // The canonical model space always appears in layoutOrder (seeded by
  // initFromSpaces) so it never needs the fallback append path.
  const inOrder = new Set(layoutOrder);
  for (const s of spaces) {
    if (!inOrder.has(s.id) && s.kind === "layout") {
      orderedSpaces.push(s);
    }
  }

  function activateSpace(spaceId: string, kind: string) {
    setActiveSpace(spaceId);
    dispatch({
      type: "shell/activate-space",
      source: "toolbar",
      spaceId,
      layoutContext: kind === "layout" ? "paper" : "model",
    });
    closeContextMenu();
  }

  // ── Multi-select (PDF-6.8) ─────────────────────────────────────────────────

  // The ordered LAYOUT-only id list — the same order the tab strip renders,
  // minus the Model tab (which is never part of a publish selection). Drives
  // shift-range select and the published page order.
  const orderedLayoutIds = orderedSpaces
    .filter((s) => s.kind === "layout")
    .map((s) => s.id);

  /**
   * Tab left-click handler. Branches on modifier keys (AutoCAD layout-tab
   * multi-select parity):
   *   - plain click  → activate the tab AND clear the multi-select; anchor it;
   *   - ctrl/cmd     → toggle this layout in the selection (no activation);
   *   - shift        → select the contiguous range anchor→target (no activation).
   * The Model tab is never multi-selectable: ctrl/shift on it just activates.
   */
  function handleTabClick(
    e: React.MouseEvent,
    spaceId: string,
    kind: string,
  ) {
    const isLayout = kind === "layout";
    const additive = e.ctrlKey || e.metaKey;
    const range = e.shiftKey;

    if (isLayout && additive) {
      e.preventDefault();
      setSelected((prev) => toggleSelection(prev, spaceId));
      setSelectionAnchor(spaceId);
      return;
    }
    if (isLayout && range) {
      e.preventDefault();
      setSelected(new Set(selectRange(orderedLayoutIds, selectionAnchor, spaceId)));
      return;
    }

    // Plain click (or any click on the Model tab): activate + reset selection.
    activateSpace(spaceId, kind);
    setSelected(new Set<string>());
    setSelectionAnchor(isLayout ? spaceId : null);
  }

  /**
   * Resolve the ordered layout ids the context-menu "Publish Selected" item
   * should publish for the right-clicked tab, and fire the publish. If the
   * right-clicked tab is not in the selection, publishes just that one tab.
   */
  function handleContextPublish() {
    if (!contextMenu) return;
    const order = resolvePublishOrder(
      orderedLayoutIds,
      selected,
      contextMenu.spaceId,
    );
    closeContextMenu();
    if (order.length === 0) return;
    void publishSelected(order);
  }

  // ── Inline rename ──────────────────────────────────────────────────────────

  function beginRename(spaceId: string, currentName: string) {
    if (spaceId === "model") return;
    setRenamingSpaceId(spaceId);
    setRenamingDraftName(currentName);
    closeContextMenu();
  }

  function commitRename() {
    const spaceId = renamingSpaceId;
    setRenamingSpaceId(null);
    setRenamingDraftName("");
    if (!spaceId) return;
    const trimmed = renamingDraftName.trim();
    const current = spaces.find((s) => s.id === spaceId);
    if (!current || !trimmed || trimmed === current.name) return;
    // Always route through shell/rename-space — the reducer validates
    // duplicate names and emits the appropriate audit/feed message
    // (layout.rename.duplicate_name warning). A synthetic commandline/submit
    // for LAYOUT R falls through routeLayoutCommand unhandled and produces
    // "command not wired" feedback instead of the intended duplicate warning.
    dispatch({ type: "shell/rename-space", source: "scene", spaceId, newName: trimmed });
  }

  function cancelRename() {
    setRenamingSpaceId(null);
    setRenamingDraftName("");
  }

  // ── Context menu ──────────────────────────────────────────────────────────

  function openContextMenu(
    e: React.MouseEvent,
    spaceId: string,
    spaceName: string,
  ) {
    e.preventDefault();
    setContextMenu({ spaceId, spaceName, x: e.clientX, y: e.clientY });
  }

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  function handleContextRename() {
    if (!contextMenu) return;
    beginRename(contextMenu.spaceId, contextMenu.spaceName);
  }

  function handleContextDelete() {
    if (!contextMenu) return;
    // LAYOUT-COMMAND-HANDLERS — routes through the same LAYOUT D arm the
    // command line uses (routeLayoutCommand → shell/remove-space + the
    // geometry-document arm). Model / last-layout guards + the
    // active-tab adjacency rule live in the shared reducers.
    dispatch({
      type: "commandline/submit",
      text: `LAYOUT D ${contextMenu.spaceName}`,
      source: "toolbar",
    });
    closeContextMenu();
  }

  function handleContextNew() {
    // LAYOUT-COMMAND-HANDLERS — routes through the LAYOUT N arm
    // (routeLayoutCommand → shell/add-space + the geometry-document
    // arm). Default name "Layout <next free N>"; the new tab is NOT
    // activated (AutoCAD parity).
    dispatch({
      type: "commandline/submit",
      text: "LAYOUT N",
      source: "toolbar",
    });
    closeContextMenu();
  }

  // ── Drag reorder (HTML5 native) ───────────────────────────────────────────

  function handleDragStart(e: React.DragEvent, spaceId: string) {
    setDragFromId(spaceId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", spaceId);
  }

  function handleDragOver(e: React.DragEvent, spaceId: string) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverId(spaceId);
  }

  function handleDrop(e: React.DragEvent, targetId: string) {
    e.preventDefault();
    const sourceId = dragFromId;
    setDragFromId(null);
    setDragOverId(null);
    if (!sourceId || sourceId === targetId) return;
    const currentOrder = orderedSpaces.map((s) => s.id);
    const fromIdx = currentOrder.indexOf(sourceId);
    const toIdx = currentOrder.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...currentOrder];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, sourceId);
    reorderLayouts(next);
  }

  function handleDragEnd() {
    setDragFromId(null);
    setDragOverId(null);
  }

  return (
    <>
      <div
        ref={containerRef}
        className="space-tabs-compact"
        data-testid="space-tabs-bar"
        onClick={contextMenu ? closeContextMenu : undefined}
      >
        {orderedSpaces.map((space) =>
          renamingSpaceId === space.id ? (
            <input
              key={space.id}
              className={`space-tab-compact is-renaming${space.id === activeSpaceId ? " is-active" : ""}`}
              data-testid={`space-tab-rename-input-${toTestIdSegment(space.id)}`}
              type="text"
              autoFocus
              value={renamingDraftName}
              placeholder={t("layout.rename.placeholder")}
              maxLength={255}
              onChange={(e) => setRenamingDraftName(e.target.value)}
              onBlur={commitRename}
              onFocus={(e) => e.currentTarget.select()}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitRename();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelRename();
                }
              }}
            />
          ) : (
            <button
              key={space.id}
              className={`space-tab-compact${space.id === activeSpaceId ? " is-active" : ""}${selected.has(space.id) ? " is-multiselected" : ""}${windowPickActive ? " is-pick-locked" : ""}${dragOverId === space.id ? " is-drag-over" : ""}`}
              type="button"
              draggable={!windowPickActive}
              // PDF-6.7 Codex P2 r3 — tabs inert during PLOT Window pick; otherwise
              // switching tabs mid-pick changes the live camera frame and the
              // persisted window ends up in the wrong layout's mm space.
              disabled={windowPickActive}
              data-testid={`space-tab-${toTestIdSegment(space.id)}`}
              onClick={(e) => {
                if (windowPickActive) return;
                handleTabClick(e, space.id, space.kind);
              }}
              onDoubleClick={() => {
                if (windowPickActive) return;
                beginRename(space.id, space.name);
              }}
              onContextMenu={(e) => {
                if (windowPickActive) {
                  e.preventDefault();
                  return;
                }
                openContextMenu(e, space.id, space.name);
              }}
              onDragStart={(e) => {
                if (windowPickActive) {
                  e.preventDefault();
                  return;
                }
                handleDragStart(e, space.id);
              }}
              onDragOver={(e) => {
                if (windowPickActive) {
                  e.preventDefault();
                  return;
                }
                handleDragOver(e, space.id);
              }}
              onDrop={(e) => {
                if (windowPickActive) {
                  e.preventDefault();
                  return;
                }
                handleDrop(e, space.id);
              }}
              onDragEnd={() => {
                if (windowPickActive) return;
                handleDragEnd();
              }}
            >
              {space.kind === "model" ? t("space.tabs.model") : space.name}
            </button>
          ),
        )}
        <ViewportStatusControls />
        <SpaceModeBadge />
      </div>

      {contextMenu
        ? (() => {
            // Right-clicked tab is a layout AND there is something to publish
            // (it is itself selected, or ≥1 layout is selected). The count is
            // the ACTUAL ordered set that would publish: the whole selection if
            // the right-clicked tab is in it, else just that one tab.
            const contextIsLayout = contextMenu.spaceId !== "model";
            const publishOrder = resolvePublishOrder(
              orderedLayoutIds,
              selected,
              contextMenu.spaceId,
            );
            const showPublish = contextIsLayout && publishOrder.length > 0;
            return (
              <div
                role="menu"
                className="space-tab-context-menu"
                data-testid="space-tab-context-menu"
                style={{ left: contextMenu.x, top: contextMenu.y }}
                onContextMenu={(e) => e.preventDefault()}
              >
                {showPublish ? (
                  <>
                    <button
                      role="menuitem"
                      type="button"
                      className="context-menu-item"
                      data-testid="space-tab-ctx-publish"
                      onClick={handleContextPublish}
                    >
                      {t("space.tabs.context.publish_selected", {
                        count: publishOrder.length,
                      })}
                    </button>
                    <div className="context-menu-divider" role="separator" />
                  </>
                ) : null}
          <button
            role="menuitem"
            type="button"
            className="context-menu-item"
            data-testid="space-tab-ctx-rename"
            disabled={contextMenu.spaceId === "model"}
            onClick={handleContextRename}
          >
            {t("space.tabs.context.rename")}
          </button>
          <button
            role="menuitem"
            type="button"
            className="context-menu-item"
            data-testid="space-tab-ctx-delete"
            disabled={contextMenu.spaceId === "model"}
            onClick={handleContextDelete}
          >
            {t("space.tabs.context.delete")}
          </button>
          <button
            role="menuitem"
            type="button"
            className="context-menu-item"
            data-testid="space-tab-ctx-new"
            onClick={handleContextNew}
          >
            {t("space.tabs.context.new")}
          </button>
              </div>
            );
          })()
        : null}
    </>
  );
}
